import { Button } from '@/app/common/components/Button'
import { Card, CardContent, CardHeader } from '@/app/common/components/Card'
import { Input } from '@/app/common/components/Input'
import PageLoader from '@/app/common/components/PageLoader/PageLoader'
import Pagination from '@/app/common/components/Pagination'
import { InfoCircleIcon, MagnifierIcon } from '@/app/common/icons/general'
import { Provider, ProvidersResponse } from '@/schemas/provider.schema'
import { Label } from '@/shad/ui/label'
import { selectProgramID } from '@/storage/program_settings/programSettingsSlice'
import { useDebounce } from '@uidotdev/usehooks'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import FamilyWizardLayout from '../../family_application/FamilyWizardLayout'
import { useGetPaymentProviders } from '../api/usePaymentProviders'

type Props = {
  onNext: (selectedProvider: Provider) => void
}

const RECORDS_PER_PAGE = 10 as const
export const SelectProvider = (props: Props) => {
  const { onNext } = props
  const [search, setSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const debouncedTerm = useDebounce(search, 500)
  const program_id = useSelector(selectProgramID)
  const [recordsPerPage, setRecordsPerPage] = useState<number>(RECORDS_PER_PAGE)

  const request = useGetPaymentProviders({
    program_id,
    search: debouncedTerm,
    page: currentPage,
    per_page: recordsPerPage,
  })

  const { t: translate } = useTranslation()

  const onSelect = (provider: Provider) => {
    onNext({
      email: provider.email,
      id: provider.id,
      location: provider.location,
      name: provider.name,
    })
  }

  useEffect(() => {
    setCurrentPage(1)
  }, [search])

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePerPageChange = (perPage: number) => {
    setRecordsPerPage(perPage)
    setCurrentPage(1)
  }
  return (
    <FamilyWizardLayout>
      <FamilyWizardLayout.Header>
        <h1>{translate('who_do_you_want_to_pay')}</h1>
      </FamilyWizardLayout.Header>
      <FamilyWizardLayout.Content className="min-w-2xl w-full">
        <Card>
          <CardHeader className="flex justify-between items-center text-md">
            <div className="relative rounded-md shadow-sm w-full">
              <Label>{translate('search_provider')}</Label>
              <Input
                onChange={(event) => setSearch(event.currentTarget.value)}
                leftIcon={<MagnifierIcon />}
                className="shadow-none"
                placeholder={translate('Eg. Google')}
              />
            </div>
          </CardHeader>

          <CardContent>
            <PageLoader query={request}>
              {(response) => {
                const providers = Array.isArray(response)
                  ? response
                  : (response as ProvidersResponse)?.data || []
                if (providers.length === 0) {
                  return (
                    <div className="font-medium text-gray-800 tracking-tight flex flex-col items-center">
                      <InfoCircleIcon className="text-2xl text-blue-500 mb-2" />
                      {translate('no_results_found')}
                    </div>
                  )
                }

                return (
                  <>
                    {(providers as Provider[]).map((provider: Provider) => (
                      <article
                        key={provider.id}
                        className="py-3 flex justify-between items-center border-b-2 border-gray-100"
                      >
                        <div>
                          <div className="font-medium text-gray-800">
                            {provider.name}
                          </div>
                          <address className="text-gray-600 text-sm not-italic">
                            {provider.location}
                          </address>
                        </div>
                        <div className="flex text-md font-bold">
                          <Button
                            variant="outline"
                            onClick={() => onSelect(provider)}
                            className="px-5"
                          >
                            {translate('select_provider')}
                          </Button>
                        </div>
                      </article>
                    ))}
                    <Pagination
                      itemsPerPage={recordsPerPage}
                      currentPage={currentPage}
                      totalRows={response?.total ?? 0}
                      onPageChange={handlePageChange}
                      onPerPageChange={handlePerPageChange}
                    />
                  </>
                )
              }}
            </PageLoader>
          </CardContent>
        </Card>
      </FamilyWizardLayout.Content>
    </FamilyWizardLayout>
  )
}
